import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Building2, ChevronLeft, Users, Trash2, UserPlus, Mail, Shield,
  Plus, ChevronRight,
} from "lucide-react";
import {
  useOrganization, useOrgMembers, useUpdateOrganization,
  useDeleteOrganization, useRemoveOrgMember, useAddOrgMember,
  useInviteToOrg, useOrgRoles, useChangeMemberRole,
  useCreateOrgRole, useDeleteOrgRole,
} from "@/hooks/use-organizations";
import {
  useOrgTeams, useEnsureCompany, useOrgCompanyUnit, useCreateOrgUnit,
  useOrgUnitMembers, useAddOrgUnitMember, useRemoveOrgUnitMember,
  useAssignMemberAppRoles, useSyncFromKeycloak,
  useBatchMemberAppRoles,
} from "@/hooks/use-org-units";
import { useAppRoles } from "@/hooks/use-app-roles";
import { useIsPlatformAdmin } from "@/lib/auth";
import { MemberRolesDialog } from "@/components/members/MemberRolesDialog";
import { Avatar, AvatarFallback, Badge, Button, Card, CardContent, CardHeader, CardTitle, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Separator, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger } from "@fox1ck-org/ui";
import { toast } from "@fox1ck-org/ui/toast";
import { UserPicker } from "@/components/shared/UserPicker";
import type { AppRoleBinding, OrgUnit, OrgUnitMember } from "@/types/org";

export default function OrganizationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isPlatformAdmin = useIsPlatformAdmin();

  const { data: org, isLoading: orgLoading } = useOrganization(id!);
  const { data: membersData, isLoading: membersLoading } = useOrgMembers(id!);
  const updateOrg = useUpdateOrganization(id!);
  const deleteOrg = useDeleteOrganization();
  const removeMember = useRemoveOrgMember(id!);
  const addMember = useAddOrgMember(id!);
  const inviteToOrg = useInviteToOrg(id!);
  const { data: orgRolesData } = useOrgRoles(id!);
  const changeMemberRole = useChangeMemberRole(id!);

  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editPlan, setEditPlan] = useState("free");
  const [editMaxMembers, setEditMaxMembers] = useState("");
  const [editStorageGB, setEditStorageGB] = useState("");
  const [showDelete, setShowDelete] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);

  const members = membersData?.members ?? [];

  function startEditing() {
    setEditName(org?.name ?? "");
    setEditDescription(org?.description ?? "");
    setEditPlan(org?.metadata?.plan ?? "free");
    setEditMaxMembers(org?.metadata?.maxMembers?.toString() ?? "");
    setEditStorageGB(org?.metadata?.storageQuotaBytes ? String(Math.round(org.metadata.storageQuotaBytes / 1_073_741_824)) : "");
    setEditing(true);
  }

  async function saveEdit() {
    try {
      await updateOrg.mutateAsync({
        name: editName,
        description: editDescription,
        plan: editPlan,
        maxMembers: editMaxMembers ? parseInt(editMaxMembers) : null,
        storageQuotaBytes: editStorageGB ? parseInt(editStorageGB) * 1_073_741_824 : null,
      });
      setEditing(false);
      toast.success("Организация обновлена");
    } catch {
      toast.error("Не удалось обновить организацию");
    }
  }

  async function handleDelete() {
    try {
      await deleteOrg.mutateAsync(id!);
      toast.success("Организация удалена");
      navigate("/organizations");
    } catch {
      toast.error("Не удалось удалить организацию");
    }
  }

  if (orgLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!org) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Организация не найдена.
      </div>
    );
  }

  const keycloakOrgId = org.metadata?.keycloakOrgId ?? org.id;
  const orgSlug = org.metadata?.slug ?? org.alias;

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/organizations"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3"
        >
          <ChevronLeft className="h-4 w-4" />
          К организациям
        </Link>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
              <Building2 className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-[17px] font-semibold text-foreground">{org.name}</h1>
              <p className="text-sm text-muted-foreground">{org.description || orgSlug}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isPlatformAdmin && !editing && (
              <>
                <Button variant="secondary" size="sm" onClick={startEditing}>
                  Изменить
                </Button>
                <Button
                  variant="danger"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setShowDelete(true)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Обзор</TabsTrigger>
          <TabsTrigger value="members">Участники ({members.length})</TabsTrigger>
          <TabsTrigger value="teams">Команды</TabsTrigger>
          <TabsTrigger value="roles">Роли</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-3 gap-6">
            <Card className="col-span-2">
              <CardHeader>
                <CardTitle className="text-sm font-medium text-muted-foreground uppercase">
                  Подробности
                </CardTitle>
              </CardHeader>
              <CardContent>
                {editing ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm text-muted-foreground mb-1">Название</label>
                      <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-sm text-muted-foreground mb-1">Описание</label>
                      <Input value={editDescription} onChange={(e) => setEditDescription(e.target.value)} />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-sm text-muted-foreground mb-1">Тариф</label>
                        <Select value={editPlan} onValueChange={setEditPlan}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="free">Free</SelectItem>
                            <SelectItem value="pro">Pro</SelectItem>
                            <SelectItem value="enterprise">Enterprise</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm text-muted-foreground mb-1">Лимит участников</label>
                        <Input type="number" value={editMaxMembers} onChange={(e) => setEditMaxMembers(e.target.value)} placeholder="Без лимита" min={1} />
                      </div>
                      <div>
                        <label className="block text-sm text-muted-foreground mb-1">Хранилище (ГБ)</label>
                        <Input type="number" value={editStorageGB} onChange={(e) => setEditStorageGB(e.target.value)} placeholder="Без лимита" min={1} />
                      </div>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button size="sm" onClick={saveEdit} disabled={updateOrg.isPending}>
                        {updateOrg.isPending ? "Сохраняем…" : "Сохранить"}
                      </Button>
                      <Button variant="secondary" size="sm" onClick={() => setEditing(false)}>Отмена</Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <InfoRow label="Название" value={org.name} />
                    <InfoRow label="Код" value={orgSlug} mono />
                    <InfoRow label="Тариф" value={<Badge variant="neutral">{org.metadata?.plan ?? "free"}</Badge>} />
                    <InfoRow label="Статус" value={<Badge variant={org.enabled ? "ok" : "danger"}>{org.enabled ? "Active" : "Disabled"}</Badge>} />
                    {org.description && <InfoRow label="Описание" value={org.description} />}
                    {org.metadata?.maxMembers && <InfoRow label="Лимит участников" value={org.metadata.maxMembers} />}
                    {org.metadata?.storageQuotaBytes && <InfoRow label="Хранилище" value={`${Math.round(org.metadata.storageQuotaBytes / 1_073_741_824)} GB`} />}
                    {org.domains && org.domains.length > 0 && (
                      <InfoRow label="Домены" value={
                        <div className="flex flex-wrap gap-1 justify-end">
                          {org.domains.map((d: any) => <Badge key={d.name} variant="neutral" className="text-xs">{d.name}</Badge>)}
                        </div>
                      } />
                    )}
                    <InfoRow label="ID в Keycloak" value={keycloakOrgId} mono small />
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium text-muted-foreground uppercase">Кратко</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground">Участники</p>
                  <p className="text-lg font-semibold text-foreground">{members.length}</p>
                </div>
                {org.membership && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-xs text-muted-foreground">Ваша роль</p>
                      <div className="flex gap-1 mt-1">
                        {org.membership.roles.map((r: string) => (
                          <Badge key={r} variant="info" className="flex items-center gap-1">
                            <Shield className="h-3 w-3" />{r}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Members Tab */}
        <TabsContent value="members">
          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm font-medium text-muted-foreground uppercase flex items-center gap-2">
                <Users className="h-4 w-4" />
                Участники организации
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="secondary" size="sm" onClick={() => setShowInvite(true)}>
                  <Mail className="h-3.5 w-3.5" /> Пригласить
                </Button>
                <Button size="sm" onClick={() => setShowAddMember(true)}>
                  <UserPlus className="h-3.5 w-3.5" /> Добавить
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {membersLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
                </div>
              ) : members.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">Участников пока нет.</p>
              ) : (
                <div className="space-y-2">
                  {members.map((member: any) => (
                    <div key={member.id} className="flex items-center justify-between px-3 py-2.5 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="text-xs">
                            {(member.firstName?.[0] ?? member.username?.[0] ?? "?").toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm text-foreground">
                            {member.firstName && member.lastName ? `${member.firstName} ${member.lastName}` : member.username}
                          </p>
                          <p className="text-xs text-muted-foreground">{member.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1">
                          {member.orgRoles.map((role: string) => <Badge key={role} variant="info">{role}</Badge>)}
                          {member.orgRoles.length === 0 && <span className="text-xs text-muted-foreground">member</span>}
                        </div>
                        {isPlatformAdmin && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary" title="Управлять ролями">
                                <Shield className="h-3.5 w-3.5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Роли организации</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              {(orgRolesData?.roles ?? []).length === 0 ? (
                                <div className="px-2 py-1.5 text-xs text-muted-foreground">Ролей нет</div>
                              ) : (
                                (orgRolesData?.roles ?? []).map((role: any) => {
                                  const hasRole = member.orgRoles.includes(role.name);
                                  return (
                                    <DropdownMenuCheckboxItem
                                      key={role.id}
                                      checked={hasRole}
                                      onCheckedChange={() => {
                                        changeMemberRole.mutate(
                                          { userId: member.id, ...(hasRole ? { revoke: [{ id: role.id, name: role.name }] } : { grant: [{ id: role.id, name: role.name }] }) },
                                          {
                                            onSuccess: () => toast.success(hasRole ? `Снята роль ${role.name}` : `Выдана роль ${role.name}`),
                                            onError: () => toast.error("Не удалось обновить роль"),
                                          }
                                        );
                                      }}
                                    >
                                      {role.name}
                                    </DropdownMenuCheckboxItem>
                                  );
                                })
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                        <Button
                          variant="ghost" size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          onClick={() => removeMember.mutate(member.id, {
                            onSuccess: () => toast.success("Участник удалён"),
                            onError: () => toast.error("Не удалось удалить участника"),
                          })}
                          title="Удалить участника"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Teams Tab */}
        <TabsContent value="teams">
          <TeamsTab
            keycloakOrgId={keycloakOrgId}
            orgName={org.name}
            orgSlug={orgSlug}
            isPlatformAdmin={isPlatformAdmin}
          />
        </TabsContent>

        {/* Roles Tab */}
        <TabsContent value="roles">
          <RolesTab orgId={id!} orgRolesData={orgRolesData} isPlatformAdmin={isPlatformAdmin} />
        </TabsContent>
      </Tabs>

      {/* Delete dialog */}
      <Dialog open={showDelete} onOpenChange={setShowDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Удалить организацию</DialogTitle>
            <DialogDescription>
              Удалить <strong className="text-foreground">{org.name}</strong>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setShowDelete(false)}>Отмена</Button>
            <Button variant="danger" onClick={handleDelete} disabled={deleteOrg.isPending}>
              {deleteOrg.isPending ? "Удаляем…" : "Удалить"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite dialog */}
      <InviteDialog open={showInvite} onClose={() => setShowInvite(false)}
        onInvite={async (email) => {
          try { await inviteToOrg.mutateAsync(email); setShowInvite(false); toast.success("Приглашение отправлено"); }
          catch { toast.error("Не удалось отправить приглашение"); }
        }}
        isPending={inviteToOrg.isPending}
      />

      {/* Add member dialog */}
      <AddOrgMemberDialog open={showAddMember} onClose={() => setShowAddMember(false)}
        onAdd={async (userId) => {
          try { await addMember.mutateAsync(userId); setShowAddMember(false); toast.success("Участник добавлен"); }
          catch { toast.error("Не удалось добавить участника"); }
        }}
        isPending={addMember.isPending}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Roles Tab (CRUD)
// ---------------------------------------------------------------------------

function RolesTab({
  orgId,
  orgRolesData,
  isPlatformAdmin,
}: {
  orgId: string;
  orgRolesData: { roles: Array<{ id: string; name: string; description?: string }> } | undefined;
  isPlatformAdmin: boolean;
}) {
  const createRole = useCreateOrgRole(orgId);
  const deleteRole = useDeleteOrgRole(orgId);
  const [showCreate, setShowCreate] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [newRoleDesc, setNewRoleDesc] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const roles = orgRolesData?.roles ?? [];

  async function handleCreate() {
    try {
      await createRole.mutateAsync({ name: newRoleName, description: newRoleDesc || undefined });
      toast.success(`Роль «${newRoleName}» создана`);
      setShowCreate(false);
      setNewRoleName("");
      setNewRoleDesc("");
    } catch {
      toast.error("Не удалось создать роль");
    }
  }

  async function handleDelete(roleName: string) {
    try {
      await deleteRole.mutateAsync(roleName);
      toast.success(`Роль «${roleName}» удалена`);
      setDeleteTarget(null);
    } catch {
      toast.error("Не удалось удалить роль");
    }
  }

  return (
    <>
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm font-medium text-muted-foreground uppercase flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Роли организации
          </CardTitle>
          {isPlatformAdmin && (
            <Button size="sm" onClick={() => setShowCreate(true)}>
              <Plus className="h-3.5 w-3.5" /> Новая роль
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {roles.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              В организации нет ролей.
              {isPlatformAdmin && " Создайте первую."}
            </p>
          ) : (
            <div className="space-y-2">
              {roles.map((role: any) => (
                <div key={role.id} className="flex items-center justify-between px-3 py-2.5 bg-muted/50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-foreground">{role.name}</p>
                    {role.description && <p className="text-xs text-muted-foreground">{role.description}</p>}
                  </div>
                  {isPlatformAdmin && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      onClick={() => setDeleteTarget(role.name)}
                      title="Удалить роль"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create role dialog */}
      <Dialog open={showCreate} onOpenChange={(v) => !v && setShowCreate(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Новая роль</DialogTitle>
            <DialogDescription>Добавить роль в организацию.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-muted-foreground mb-1">Название роли</label>
              <Input value={newRoleName} onChange={(e) => setNewRoleName(e.target.value)} placeholder="например, editor" />
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-1">Описание</label>
              <Input value={newRoleDesc} onChange={(e) => setNewRoleDesc(e.target.value)} placeholder="Описание, если нужно" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setShowCreate(false)}>Отмена</Button>
            <Button onClick={handleCreate} disabled={!newRoleName || createRole.isPending}>
              {createRole.isPending ? "Создаём…" : "Новая роль"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete role confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Удалить роль</DialogTitle>
            <DialogDescription>
              Удалить <strong className="text-foreground">{deleteTarget}</strong>? Участники с этой ролью её потеряют.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>Отмена</Button>
            <Button variant="danger" onClick={() => deleteTarget && handleDelete(deleteTarget)} disabled={deleteRole.isPending}>
              {deleteRole.isPending ? "Удаляем…" : "Удалить"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Teams Tab
// ---------------------------------------------------------------------------

function TeamsTab({
  keycloakOrgId,
  orgName,
  orgSlug,
  isPlatformAdmin,
}: {
  keycloakOrgId: string;
  orgName: string;
  orgSlug: string;
  isPlatformAdmin: boolean;
}) {
  const { data: teamsData, isLoading } = useOrgTeams(keycloakOrgId);
  const { data: companyUnit, isLoading: companyLoading } = useOrgCompanyUnit(keycloakOrgId);
  const ensureCompany = useEnsureCompany();
  const syncFromKC = useSyncFromKeycloak();
  const { data: appRolesData } = useAppRoles();

  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [expandedTeam, setExpandedTeam] = useState<string | null>(null);
  const [autoSetupDone, setAutoSetupDone] = useState(false);

  const teams = teamsData?.items ?? [];
  const companyUnitId = companyUnit?.id ?? null;

  // Auto-provision company unit for existing orgs that don't have one
  useEffect(() => {
    if (companyLoading || companyUnit || autoSetupDone || !keycloakOrgId) return;
    setAutoSetupDone(true);
    (async () => {
      try {
        await ensureCompany.mutateAsync({
          keycloakOrgId,
          name: orgName,
          slug: orgSlug.toLowerCase().replace(/[^a-z0-9-]/g, "-"),
        });
        await syncFromKC.mutateAsync();
      } catch {
        // Non-fatal — user can still create teams manually
      }
    })();
  }, [companyLoading, companyUnit, autoSetupDone, keycloakOrgId]);

  // App bindings that target "team" unit type — pass full binding (status included)
  // so per-role provisioning state can drive UI affordances.
  const appBindings: AppRoleBinding[] = (appRolesData?.items ?? []).filter((b) =>
    b.orgUnitTypes?.includes("team")
  );

  if (isLoading || companyLoading || (ensureCompany.isPending && !autoSetupDone)) {
    return <div className="space-y-3"><Skeleton className="h-20 w-full" /><Skeleton className="h-20 w-full" /></div>;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <p className="text-sm text-muted-foreground">команд: {teams.length}</p>
          <Link to="/org-structure" className="text-xs text-muted-foreground hover:text-primary">
            Открыть в дереве &rarr;
          </Link>
        </div>
        {isPlatformAdmin && companyUnitId && (
          <Button size="sm" onClick={() => setShowCreateTeam(true)}>
            <Plus className="h-4 w-4" /> Новая команда
          </Button>
        )}
      </div>

      {/* Team list */}
      {teams.length === 0 ? (
        <Card>
          <CardContent className="text-center py-8">
            <Users className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground mb-3">Команд пока нет. Создайте первую, чтобы объединить участников.</p>
            {isPlatformAdmin && companyUnitId && (
              <Button size="sm" onClick={() => setShowCreateTeam(true)}>
                <Plus className="h-4 w-4" /> Новая команда
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {teams.map((team) => (
            <TeamRow
              key={team.id}
              team={team}
              expanded={expandedTeam === team.id}
              onToggle={() => setExpandedTeam(expandedTeam === team.id ? null : team.id)}
              isPlatformAdmin={isPlatformAdmin}
              appBindings={appBindings}
            />
          ))}
        </div>
      )}

      {/* Create team dialog */}
      {showCreateTeam && companyUnitId && (
        <CreateTeamDialog
          companyUnitId={companyUnitId}
          onClose={() => setShowCreateTeam(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Team Row (expandable)
// ---------------------------------------------------------------------------

function TeamRow({
  team,
  expanded,
  onToggle,
  isPlatformAdmin,
  appBindings,
}: {
  team: OrgUnit;
  expanded: boolean;
  onToggle: () => void;
  isPlatformAdmin: boolean;
  appBindings: AppRoleBinding[];
}) {
  const { data: membersData, isLoading: membersLoading } = useOrgUnitMembers(expanded ? team.id : "");
  const removeMember = useRemoveOrgUnitMember(team.id);
  const clientIds = appBindings.map((b) => b.clientId);
  const { data: batchRolesData } = useBatchMemberAppRoles(expanded ? team.id : "", clientIds);

  const [showAddMember, setShowAddMember] = useState(false);
  const [rolesForMember, setRolesForMember] = useState<OrgUnitMember | null>(null);

  const teamMembers: OrgUnitMember[] = membersData?.members ?? [];
  const batchRoles = batchRolesData?.roles ?? {};

  return (
    <Card>
      {/* Team header — clickable */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors rounded-t-xl"
      >
        <div className="flex items-center gap-3">
          <ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform ${expanded ? "rotate-90" : ""}`} />
          <div className="text-left">
            <p className="text-sm font-medium text-foreground capitalize">{team.name}</p>
            <p className="text-xs text-muted-foreground">{team.keycloakGroupPath ?? team.slug}</p>
          </div>
        </div>
        <Badge variant="neutral" className="text-xs">
          участников: {team.memberCount ?? "?"}
        </Badge>
      </button>

      {/* Expanded content */}
      {expanded && (
        <CardContent className="pt-0 border-t">
          <div className="flex items-center justify-between py-2">
            <p className="text-xs text-muted-foreground uppercase font-medium">Участники команды</p>
            {isPlatformAdmin && (
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowAddMember(true)}>
                <UserPlus className="h-3 w-3" /> Добавить участника
              </Button>
            )}
          </div>

          {membersLoading ? (
            <div className="space-y-2"><Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" /></div>
          ) : teamMembers.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">В команде пока нет участников.</p>
          ) : (
            <div className="space-y-1.5">
              {teamMembers.map((member) => {
                // Collect all roles for this member from batch data
                const memberRoleMap = batchRoles[member.id] ?? {};
                const allRoles = Object.values(memberRoleMap).flat();
                return (
                  <TeamMemberRow
                    key={member.id}
                    member={member}
                    roles={allRoles}
                    isPlatformAdmin={isPlatformAdmin}
                    onManageRoles={() => setRolesForMember(member)}
                    onRemove={() => {
                      removeMember.mutate(member.id, {
                        onSuccess: () => toast.success("Удалён из команды"),
                        onError: () => toast.error("Не удалось удалить участника"),
                      });
                    }}
                  />
                );
              })}
            </div>
          )}

          {/* Add member to team dialog */}
          {showAddMember && (
            <AddTeamMemberDialog
              teamId={team.id}
              appBindings={appBindings}
              onClose={() => setShowAddMember(false)}
            />
          )}

          {/* Manage roles dialog */}
          {rolesForMember && (
            <MemberRolesDialog
              unitId={team.id}
              member={rolesForMember}
              appBindings={appBindings}
              onClose={() => setRolesForMember(null)}
            />
          )}
        </CardContent>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Team Member Row (with inline role badges)
// ---------------------------------------------------------------------------

function TeamMemberRow({
  member,
  roles,
  isPlatformAdmin,
  onManageRoles,
  onRemove,
}: {
  member: OrgUnitMember;
  roles: string[];
  isPlatformAdmin: boolean;
  onManageRoles: () => void;
  onRemove: () => void;
}) {
  const displayName = member.firstName || member.lastName
    ? `${member.firstName ?? ""} ${member.lastName ?? ""}`.trim()
    : member.username;

  return (
    <div className="flex items-center justify-between px-3 py-2 bg-muted/40 rounded-lg">
      <div className="flex items-center gap-2.5">
        <Avatar className="h-7 w-7">
          <AvatarFallback className="text-xs">
            {(member.firstName?.[0] ?? member.username?.[0] ?? "?").toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <p className="text-sm text-foreground truncate">{displayName}</p>
          {member.email && <p className="text-xs text-muted-foreground truncate">{member.email}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {roles.length > 0 ? (
          <div className="flex gap-1">
            {roles.map((r) => <Badge key={r} variant="info" className="text-xs">{r}</Badge>)}
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">ролей нет</span>
        )}
        {isPlatformAdmin && (
          <>
            <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-primary" onClick={onManageRoles} title="Роли приложений">
              <Shield className="h-3 w-3" />
            </Button>
            <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={onRemove} title="Удалить из команды">
              <Trash2 className="h-3 w-3" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create Team Dialog
// ---------------------------------------------------------------------------

function CreateTeamDialog({ companyUnitId, onClose }: { companyUnitId: string; onClose: () => void }) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const createTeam = useCreateOrgUnit();

  function autoSlug(val: string) {
    setName(val);
    setSlug(val.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""));
  }

  async function handleCreate() {
    try {
      await createTeam.mutateAsync({ name, slug, type: "team", parentId: companyUnitId });
      toast.success(`Команда «${name}» создана`);
      onClose();
    } catch {
      toast.error("Не удалось создать команду");
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Новая команда</DialogTitle>
          <DialogDescription>Команды объединяют участников и определяют, что им видно в приложениях.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="block text-sm text-muted-foreground mb-1">Название команды</label>
            <Input value={name} onChange={(e) => autoSlug(e.target.value)} placeholder="например, Маркетинг" />
          </div>
          <div>
            <label className="block text-sm text-muted-foreground mb-1">Код</label>
            <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="marketing" className="font-mono" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Отмена</Button>
          <Button onClick={handleCreate} disabled={!name || !slug || createTeam.isPending}>
            {createTeam.isPending ? "Создаём…" : "Новая команда"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Add Team Member Dialog (with role selection)
// ---------------------------------------------------------------------------

function AddTeamMemberDialog({
  teamId,
  appBindings,
  onClose,
}: {
  teamId: string;
  appBindings: AppRoleBinding[];
  onClose: () => void;
}) {
  const [userId, setUserId] = useState<string | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<Record<string, string[]>>({});
  const addMember = useAddOrgUnitMember(teamId);
  const assignRoles = useAssignMemberAppRoles(teamId);
  const [submitting, setSubmitting] = useState(false);

  function toggleRole(clientId: string, roleName: string) {
    setSelectedRoles((prev) => {
      const current = prev[clientId] ?? [];
      const has = current.includes(roleName);
      return { ...prev, [clientId]: has ? current.filter((r) => r !== roleName) : [...current, roleName] };
    });
  }

  async function handleAdd() {
    if (!userId) return;
    setSubmitting(true);
    try {
      await addMember.mutateAsync(userId);
      for (const [clientId, roles] of Object.entries(selectedRoles)) {
        if (roles.length > 0) await assignRoles.mutateAsync({ userId, clientId, roles });
      }
      toast.success("Участник добавлен с ролями");
      onClose();
    } catch (err) {
      toast.error(`Ошибка: ${err instanceof Error ? err.message : "неизвестная ошибка"}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Добавить участника команды</DialogTitle>
          <DialogDescription>Выберите пользователя и назначьте ему роли приложений.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-muted-foreground mb-1">Пользователь</label>
            <UserPicker value={userId} onChange={setUserId} placeholder="Поиск пользователей…" />
          </div>
          {userId && appBindings.length > 0 && (
            <div>
              <label className="block text-sm text-muted-foreground mb-2">Роли приложений</label>
              <div className="space-y-3">
                {appBindings.map((binding) => (
                  <div key={binding.clientId}>
                    <p className="text-xs font-medium text-foreground mb-1.5">{binding.clientId}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {binding.roles.map((role) => {
                        const active = (selectedRoles[binding.clientId] ?? []).includes(role.name);
                        return (
                          <Button key={role.name} type="button" size="sm" variant={active ? "primary" : "outline"} className="text-xs h-7"
                            onClick={() => toggleRole(binding.clientId, role.name)} title={role.description}
                          >
                            {role.name}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Отмена</Button>
          <Button onClick={handleAdd} disabled={!userId || submitting}>
            {submitting ? "Добавляем…" : "Добавить участника"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function InfoRow({ label, value, mono, small }: { label: string; value: React.ReactNode; mono?: boolean; small?: boolean }) {
  return (
    <>
      <div className="flex justify-between items-center">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span className={`text-sm text-foreground ${mono ? "font-mono" : ""} ${small ? "text-xs break-all max-w-[200px] text-right" : ""}`}>
          {value}
        </span>
      </div>
      <Separator />
    </>
  );
}

function InviteDialog({ open, onClose, onInvite, isPending }: { open: boolean; onClose: () => void; onInvite: (email: string) => Promise<void>; isPending: boolean }) {
  const [email, setEmail] = useState("");
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Пригласить по почте</DialogTitle></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); onInvite(email); }}>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="user@example.com" className="mb-4" />
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>Отмена</Button>
            <Button type="submit" disabled={!email || isPending}>{isPending ? "Отправляем…" : "Отправить приглашение"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddOrgMemberDialog({ open, onClose, onAdd, isPending }: { open: boolean; onClose: () => void; onAdd: (userId: string) => Promise<void>; isPending: boolean }) {
  const [userId, setUserId] = useState<string | null>(null);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Добавить участника</DialogTitle></DialogHeader>
        <div className="mb-4">
          <label className="block text-sm text-muted-foreground mb-1">Пользователь</label>
          <UserPicker value={userId} onChange={setUserId} placeholder="Поиск пользователей…" />
        </div>
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onClose}>Отмена</Button>
          <Button onClick={() => userId && onAdd(userId)} disabled={!userId || isPending}>{isPending ? "Добавляем…" : "Добавить участника"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
